/**
 * 검색·키워드·브랜드 모니터링 ES filter 공통 조각
 */

/** filter 조각 한 개 — 소비처(server/moments·훅)가 이 모양을 그대로 싣는다 */
// ⚠️ 교차(& Record)로 적으면 소비처의 `ElsFilterDescriptor[]`(닫힌 type 별칭)가 대입 불가다 —
// 인라인 인덱스 시그니처면 type 별칭의 암시적 인덱스로 서로 통과한다.
export type MonitoringFilter = { type?: string; field?: string; value?: unknown; [key: string]: unknown }


function buildPostDateRangeFilter(startDate: unknown, endDate: unknown) {
  return { type: 'range', field: 'postDate', value: [startDate, endDate] };
}

function appendPlatformFilter(filters: MonitoringFilter[], platformIds: unknown[] = []) {
  if (!Array.isArray(platformIds) || platformIds.length === 0) return filters;
  filters.push({ type: 'terms', field: 'source', value: platformIds });
  return filters;
}

function appendCountryFilter(filters: MonitoringFilter[], countryCodes: unknown[] = []) {
  if (!Array.isArray(countryCodes) || countryCodes.length === 0) return filters;
  filters.push({
    type: 'terms',
    field: 'market',
    value: countryCodes.map((c) => String(c).toUpperCase()),
  });
  return filters;
}

/** 컨텐츠 언어 필터 — tL terms (소문자 ISO 639-1 코드) */
function appendLanguageFilter(filters: MonitoringFilter[], languageCodes: unknown[] = []) {
  if (!Array.isArray(languageCodes) || languageCodes.length === 0) return filters;
  const normalized = languageCodes
    .map((c) => String(c).toLowerCase())
    .filter(Boolean);
  if (normalized.length === 0) return filters;
  filters.push({
    type: 'terms',
    field: 'tL',
    value: normalized,
  });
  return filters;
}

function appendBeautyEvalFilter(filters: MonitoringFilter[], enabled = true) {
  if (enabled) {
    filters.push({ type: 'term', field: 'eval', value: 'in' });
  }
  return filters;
}

/**
 * 총도달 하한.
 *
 * ⚠️ 대상 필드가 `useMetricScoreSort`에 따라 갈린다 — **인기순 정렬 필드와 같은 스위치여야
 * 한다.** 어긋나면 "하한을 건 필드"와 "정렬한 필드"가 달라진다.
 *
 * @param {Object[]} filters
 * @param {number|null|undefined} metricMin `null`은 "하한 없음"이고 `0`과 다르다
 * @param {Object} [options]
 * @param {boolean} [options.useMetricScoreSort] search·keyword `true`, brand `false`
 * @returns {Object[]}
 */
export function appendMetricMinFilter(filters: MonitoringFilter[], metricMin: unknown, { useMetricScoreSort = false }: { useMetricScoreSort?: boolean } = {}) {
  if (typeof metricMin === 'number' && Number.isFinite(metricMin) && metricMin >= 0) {
    filters.push({
      type: 'range',
      field: useMetricScoreSort ? 'metricScoreSort' : 'metricScore',
      value: [metricMin, null],
    });
  }
  return filters;
}

/**
 * 페르소나 칩 — 단일 선택 시 기존 규칙, 복수 선택 시 OR(should)
 * 일반/광고·협찬/브랜드 계정은 상호 배타적이며, 다중 선택은 합집합(OR)이다.
 */
function buildPersonaShouldClauses({ personaAdsEval = [], personaIsOfficial = false }: { personaAdsEval?: boolean[]; personaIsOfficial?: boolean } = {}) {
  const adsEvalValues = Array.isArray(personaAdsEval)
    ? personaAdsEval.filter((v) => v === true || v === false)
    : [];
  const wantsOfficial = personaIsOfficial === true;
  const hasOrganic = adsEvalValues.includes(false);
  const hasSponsored = adsEvalValues.includes(true);

  if (hasOrganic && hasSponsored && wantsOfficial) return [];

  if (adsEvalValues.length === 0 && !wantsOfficial) return [];

  const should = [];
  if (hasOrganic) {
    should.push({
      bool: {
        must: [{ term: { adsEval: false } }],
        must_not: [{ term: { isOfficialAccount: true } }],
      },
    });
  }
  if (hasSponsored) {
    should.push({
      bool: {
        must: [{ term: { adsEval: true } }],
        must_not: [{ term: { isOfficialAccount: true } }],
      },
    });
  }
  if (wantsOfficial) {
    should.push({ term: { isOfficialAccount: true } });
  }
  return should;
}

/**
 * 클라이언트 필터 — 선택된 페르소나 집합에 포스트가 포함되는지 (OR, 전체 선택 시 항상 true)
 * @param {*} post
 * @param {{ personaAdsEval?: boolean[], personaIsOfficial?: boolean }} [selection]
 *   기본값 `[]`만 있으면 TS가 `never[]`로 못 박아 TS 호출부(post-filter.ts)가 막힌다
 */
export function isPostPersonaSelected(
  rawPost: unknown,
  { personaAdsEval = [], personaIsOfficial = false }: { personaAdsEval?: boolean[]; personaIsOfficial?: boolean } = {},
) {
  // 소비처(moment-filter)가 제네릭 T 를 그대로 넘긴다 — 입은 넓게, 안에서 좁힌다(util-user 규약)
  const post = rawPost as { isOfficialAccount?: unknown; adsEval?: unknown } | null | undefined;
  const adsEvalValues = Array.isArray(personaAdsEval)
    ? personaAdsEval.filter((v) => v === true || v === false)
    : [];
  const wantsOfficial = personaIsOfficial === true;
  const hasOrganic = adsEvalValues.includes(false);
  const hasSponsored = adsEvalValues.includes(true);

  if (hasOrganic && hasSponsored && wantsOfficial) return true;
  if (adsEvalValues.length === 0 && !wantsOfficial) return true;

  const isOfficial = post?.isOfficialAccount === true;
  if (wantsOfficial && isOfficial) return true;
  if (!isOfficial) {
    if (hasOrganic && post?.adsEval === false) return true;
    if (hasSponsored && post?.adsEval === true) return true;
  }
  return false;
}

/**
 * ⚠️ 기본값 `= []`만으로는 TS 호출부가 **아무 값도 넘길 수 없다** — `never[]`로 추론된다.
 * 주석만 더한 것이고 실행되는 줄은 그대로다.
 *
 * @param {Object[]} filters
 * @param {Object} [options]
 * @param {boolean[]} [options.personaAdsEval] `true`=광고·협찬, `false`=일반
 * @param {boolean} [options.personaIsOfficial] 브랜드 계정만
 * @returns {Object[]}
 */
export function appendPersonaFilters(
  filters: MonitoringFilter[],
  { personaAdsEval = [], personaIsOfficial = false }: { personaAdsEval?: boolean[]; personaIsOfficial?: boolean } = {},
) {
  const adsEvalValues = Array.isArray(personaAdsEval)
    ? personaAdsEval.filter((v) => v === true || v === false)
    : [];
  const wantsOfficial = personaIsOfficial === true;
  const hasOrganic = adsEvalValues.includes(false);
  const hasSponsored = adsEvalValues.includes(true);

  if (hasOrganic && hasSponsored && wantsOfficial) return filters;

  if (adsEvalValues.length === 0 && !wantsOfficial) return filters;

  const selectedCount =
    (hasOrganic ? 1 : 0) + (hasSponsored ? 1 : 0) + (wantsOfficial ? 1 : 0);

  if (selectedCount === 1) {
    if (wantsOfficial) {
      filters.push({ type: 'term', field: 'isOfficialAccount', value: true });
      return filters;
    }
    filters.push({ type: 'term', field: 'adsEval', value: adsEvalValues[0] });
    filters.push({ type: 'must_not_term', field: 'isOfficialAccount', value: true });
    return filters;
  }

  const should = buildPersonaShouldClauses({ personaAdsEval, personaIsOfficial });

  if (should.length === 0) return filters;

  filters.push({
    type: 'bool_should',
    query: {
      bool: {
        should,
        minimum_should_match: 1,
      },
    },
  });

  return filters;
}

/**
 * 키워드·브랜드 공통 filter 베이스 (기간·플랫폼·국가·eval·metric·페르소나)
 *
 * ⚠️ 아래 `@param`은 **TS 호출부를 위한 것이다.** 기본값 `[]`·`null`만 있으면 TS가
 * `never[]`·`null`로 못 박아 실제 값을 넘길 수 없다.
 *
 * @param {object} [input]
 * @param {*} [input.startDate] 조회 시작 (YYYYMMDD 숫자·dayjs·Date·문자열)
 * @param {*} [input.endDate] 조회 끝
 * @param {string[]} [input.platformIds]
 * @param {string[]} [input.countryCodes] ⚠️ 안에서 `toUpperCase()` 한다
 * @param {string[]} [input.languageCodes]
 * @param {boolean} [input.aiBeautyFilterEnabled]
 * @param {number|null} [input.metricMin] `null`은 "하한 없음"이고 `0`과 다르다
 * @param {boolean} [input.useMetricScoreSort] search·keyword `true`, brand `false`
 * @param {boolean[]} [input.personaAdsEval]
 * @param {boolean} [input.personaIsOfficial]
 * @param {any[]} [input.extraFilters] 화면이 미리 만든 절 (계정·채널 등)
 * @returns {import('src/utils/moments/els/moment-query').ElsClause[]} ES filter 서술자 배열.
 *   ⚠️ `ElsClause = Record<string, unknown>` 이라 서술자와 ES 절 양쪽을 다 담는다 — 타입이
 *   둘을 갈라 주지 않으니, 넘기는 쪽이 어느 모양인지 확인해야 한다.
 */
export function buildCommonMonitoringFilters({
  startDate,
  endDate,
  platformIds = [],
  countryCodes = [],
  languageCodes = [],
  aiBeautyFilterEnabled = true,
  metricMin = null,
  /** keyword·search: metricScoreSort, brand: metricScore */
  useMetricScoreSort = false,
  personaAdsEval = [],
  personaIsOfficial = false,
  extraFilters = [],
}: {
  startDate?: unknown;
  endDate?: unknown;
  platformIds?: unknown[];
  countryCodes?: unknown[];
  languageCodes?: unknown[];
  aiBeautyFilterEnabled?: boolean;
  metricMin?: number | null;
  useMetricScoreSort?: boolean;
  personaAdsEval?: boolean[];
  personaIsOfficial?: boolean;
  extraFilters?: MonitoringFilter[];
} = {}) {
  const filters: MonitoringFilter[] = [...extraFilters];

  if (startDate != null && endDate != null) {
    filters.unshift(buildPostDateRangeFilter(startDate, endDate));
  }

  appendBeautyEvalFilter(filters, aiBeautyFilterEnabled);
  appendPlatformFilter(filters, platformIds);
  appendCountryFilter(filters, countryCodes);
  appendLanguageFilter(filters, languageCodes);
  appendMetricMinFilter(filters, metricMin, { useMetricScoreSort });
  appendPersonaFilters(filters, { personaAdsEval, personaIsOfficial });

  return filters;
}
